import { useState, useCallback, useEffect } from "react";
import {
  CreateThreadRequest,
  PersistentThread,
  ThreadsPaginatedResponse,
  UpdateThreadRequest,
  threadsCreateThread,
  threadsDeleteThread,
  threadsUpdateThread,
  usersGetUserThreads,
} from "../sdk";

/**
 * Hook for managing threads with optional organization filtering
 * @param userId - Required user ID
 * @param projectId - Optional project ID to filter threads
 * @param orgId - Optional organization ID to filter threads
 * @returns Thread management functions and state
 */
export function useThreads(
  userId: string,
  projectId?: string,
  orgId?: string
): {
  threads: PersistentThread[];
  loading: boolean;
  error: Error | null;
  createThread: (
    requestBody: CreateThreadRequest
  ) => Promise<PersistentThread | undefined>;
  updateThread: (
    threadId: string,
    requestBody: UpdateThreadRequest
  ) => Promise<void>;
  deleteThread: (threadId: string) => Promise<void>;
  refreshThreads: () => Promise<void>;
} {
  const [threads, setThreads] = useState<PersistentThread[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchThreads = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await usersGetUserThreads({
        path: { user_id: userId },
        query: {
          project_id: projectId,
          org_id: orgId,
        },
      });
      const data = response.data;
      if (data && 'results' in data) {
        setThreads(data.results);
      } else {
        setThreads([]);
      }
      setError(null);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId, orgId]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const createThread = useCallback(
    async (
      requestBody: CreateThreadRequest
    ): Promise<PersistentThread | undefined> => {
      try {
        const response = await threadsCreateThread({
          body: requestBody,
        });
        const newThread = response.data;
        if (newThread) {
          setThreads((prevThreads) => [...prevThreads, newThread]);
          return newThread;
        }
        return undefined;
      } catch (err: any) {
        setError(err);
        return undefined;
      }
    },
    []
  );

  const updateThread = useCallback(
    async (
      threadId: string,
      requestBody: UpdateThreadRequest
    ): Promise<void> => {
      try {
        const response = await threadsUpdateThread({
          path: { thread_id: threadId },
          body: requestBody,
        });
        const updatedThread = response.data;
        if (updatedThread) {
          setThreads((prevThreads) =>
            prevThreads.map((thread) =>
              thread.id === threadId ? updatedThread : thread
            )
          );
        }
      } catch (err: any) {
        setError(err);
      }
    },
    []
  );

  const deleteThread = useCallback(async (threadId: string): Promise<void> => {
    try {
      await threadsDeleteThread({ path: { thread_id: threadId } });
      setThreads((prevThreads) =>
        prevThreads.filter((thread) => thread.id !== threadId)
      );
    } catch (err: any) {
      setError(err);
    }
  }, []);

  return {
    threads,
    loading,
    error,
    createThread,
    updateThread,
    deleteThread,
    refreshThreads: fetchThreads,
  };
}
